import { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useAuditLog } from '../../hooks/useAuditLog';
import type { AuditRecord } from '../../api/adminAudit';
import { formatDistanceToNow } from 'date-fns';

const entityLabels: Record<AuditRecord['entity'], string> = {
  candidate: 'Candidate',
  employer: 'Employer',
  job: 'Job',
  lead: 'Lead',
};

export function AuditViewer() {
  const params = useParams<{ entity: AuditRecord['entity']; entityId: string }>();
  const [searchParams] = useSearchParams();
  const entity = params.entity ?? searchParams.get('entity') ?? 'candidate';
  const entityId = params.entityId ?? searchParams.get('entityId') ?? '';
  const [expanded, setExpanded] = useState<string | null>(null);

  const { data, isLoading, isError } = useAuditLog(entity as AuditRecord['entity'], entityId);

  if (!entityId) {
    return (
      <div className="min-h-screen bg-[#05060f] pb-24 text-white">
        <div className="mx-auto max-w-4xl px-4 py-16">
          <p>Select an entity to view audit history.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#05060f] pb-24 text-white">
      <div className="mx-auto max-w-4xl px-4 py-16 space-y-6">
        <div>
          <p className="text-xs uppercase tracking-[0.4em] text-[#f7ef8a]">Admin · Audit</p>
          <h1 className="mt-2 text-3xl font-semibold text-white">Audit history</h1>
          <p className="text-sm text-gray-400">
            {entityLabels[entity as AuditRecord['entity']]} · {entityId}
          </p>
        </div>
        {isLoading && <p className="text-gray-400">Loading audit events…</p>}
        {isError && <p className="text-red-300">Unable to load audit history.</p>}
        {!isLoading && !isError && (data?.length ?? 0) === 0 && (
          <Card className="border-white/10 bg-white/[0.02]">
            <CardContent className="p-10 text-center text-sm text-gray-400">No audit events recorded yet.</CardContent>
          </Card>
        )}
        {(data ?? []).map((record) => (
          <Card key={record.id} className="border-white/10 bg-white/[0.03]">
            <CardHeader className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg text-white">{record.summary}</CardTitle>
                <p className="text-xs text-gray-500">
                  {formatDistanceToNow(new Date(record.createdAt), { addSuffix: true })} · {record.actor}
                </p>
              </div>
              <Badge className="border-white/10 bg-white/[0.07] text-gray-200">{record.action.replace('_', ' ')}</Badge>
            </CardHeader>
            <CardContent className="space-y-4 p-6 pt-0 text-sm text-gray-200">
              {record.note && <p className="text-gray-300">{record.note}</p>}
              {(record.before || record.after) && (
                <div className="flex flex-wrap gap-4">
                  {record.before && (
                    <pre className="w-full overflow-auto rounded-md border border-white/10 bg-black/30 p-3 text-xs text-gray-300 sm:w-[calc(50%-0.5rem)]">
                      <strong className="text-white">Before</strong>
                      <br />
                      {JSON.stringify(record.before, null, 2)}
                    </pre>
                  )}
                  {record.after && (
                    <pre className="w-full overflow-auto rounded-md border border-white/10 bg-black/30 p-3 text-xs text-gray-300 sm:w-[calc(50%-0.5rem)]">
                      <strong className="text-white">After</strong>
                      <br />
                      {JSON.stringify(record.after, null, 2)}
                    </pre>
                  )}
                </div>
              )}
              <Button
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10"
                onClick={() => setExpanded((prev) => (prev === record.id ? null : record.id))}
              >
                {expanded === record.id ? 'Hide raw event' : 'View raw event'}
              </Button>
              {expanded === record.id && (
                <pre className="overflow-auto rounded-md border border-white/10 bg-black/50 p-3 text-xs text-gray-300">
                  {JSON.stringify(record, null, 2)}
                </pre>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
